/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The focused pick inside the desktop panel (2026-10-18, one desktop panel): what the action
 * row's Measurements and Owners open at `lg` and up, in the side panel the pins live in. The
 * "This pick" segment is the pick's lab panel — its curves, the reader's declared pair overlaid,
 * the four tabs, the band chips; "Owners" is its owner reviews. The phone sheet (`PickSheet`)
 * shows the same bodies in its own chrome (three segments since 2026-10-19, Compare included);
 * a change to what a segment shows belongs in `PickLabPanel` / `PickOwners` / `LabCompareBody`,
 * which both read.
 *
 * The foot (2026-10-19) is `PickFocusFoot`, rendered by the drawer below its scroll region on all
 * three segments, so it stays in view however long the segment is. "Full review ›" left it then
 * (product call). It holds the pin tile and, on its right, the way to `/compare` (`CompareTile`),
 * half the width each — the phone sheet's foot too (2026-10-30). The tile replaces the body's
 * text links, so `LabCompareBody` drops its own "Open the full comparison →" under a joined pick.
 *
 * Client-only: the panel mounts on a click (`LabCompareDrawer` is `next/dynamic`, `ssr: false`),
 * so nothing here is in the edge-cached HTML (#51). The owners segment prints a pick's notes a
 * second time on the page (the roster row's copy is in the HTML) — each note is still served once.
 */
import styles from './PickFocusBody.module.css'
import PickLabPanel from './PickLabPanel'
import PickOwners from './PickOwners'
import PinToCompare from '../PinToCompare'
import pinStyles from '../PinToCompare.module.css'
import clsx from 'clsx'
import { publicConfig } from 'config'
import { compareHref, productSlugFromHref } from 'lib/dealhunt/url'
import { useOwnerTrack } from 'lib/owner-notes/analytics'
import { GUIDE_COPY } from 'lib/owner-notes/band-copy'
import type { GuidePickContext } from 'lib/dealhunt/types'
import { cardOwnersFromCard } from 'lib/owner-notes/card-owners'
import { useCurves } from 'lib/owner-notes/curves'
import { useDeclaredContext, type DeclaredProduct } from 'lib/owner-notes/declared'
import { pickIsYours, pickNumbers, wantsCurves } from 'lib/owner-notes/guide-pick'
import {
  MAX_PINS,
  compareTokensFor,
  useLabCompare,
  type LabTarget,
  type PanelSegment,
  type PickFocus,
} from 'lib/owner-notes/lab-compare'

interface PickFocusBodyProps {
  focus: PickFocus
  declared: DeclaredProduct | null
}

export interface PickCompareLink {
  href: string
  label: string
  /** Every product the link opens, in its order: the tile names them. */
  names: string[]
}

/**
 * Where the foot's compare tile goes: exactly what the segment shows (2026-10-30). "This pick"
 * and Owners: yours (unless this pick is it), then this pick. Compare: yours, then the pins —
 * the focused pick only once it is pinned. `maxPins` keeps that within `/compare`'s three. Null
 * for a pick DealHunt hasn't joined, or a Compare with nothing on it.
 */
export function pickCompareLink(
  segment: PanelSegment,
  declared: DeclaredProduct | null,
  context: GuidePickContext | null,
  yours: boolean,
  pinned: readonly LabTarget[],
  trailingSlash: boolean
): PickCompareLink | null {
  if (!context) {
    return null
  }
  const products =
    segment === 'compare'
      ? pinned
      : [{ nodeId: context.node_id, pdpHref: context.sg_href, name: context.name }]
  const mine = yours && segment !== 'compare' ? null : declared
  const all = [
    ...(mine?.nodeId ? [{ nodeId: mine.nodeId, pdpHref: mine.href, name: mine.name }] : []),
    ...products,
  ]
  const tokens = compareTokensFor(null, all)
  if (!tokens.length) {
    return null
  }
  const names = tokens.map(
    t => all.find(p => (productSlugFromHref(p.pdpHref) ?? p.nodeId) === t)?.name ?? t
  )
  let label: string = GUIDE_COPY.panel.compare
  if (segment === 'compare' && tokens.length > 1) {
    label = GUIDE_COPY.panel.compareThese(tokens.length)
  } else if (segment !== 'compare' && mine) {
    label = GUIDE_COPY.panel.compareYours
  }
  return { href: compareHref(tokens.slice(0, MAX_PINS), trailingSlash), label, names }
}

/** The foot's right half: the pin's tile in /compare's blue, opening what the segment shows
 *  (`pickCompareLink`) and naming it. */
export const CompareTile: React.VFC<{
  segment: PanelSegment
  context: GuidePickContext | null
  yours: boolean
}> = ({ segment, context, yours }) => {
  const declared = useDeclaredContext()?.declared ?? null
  const pinned = useLabCompare()?.pinned ?? []
  const link = pickCompareLink(
    segment,
    declared,
    context,
    yours,
    pinned,
    Boolean(publicConfig('useTrailingSlash'))
  )
  if (!link) {
    return null
  }
  const names = link.names.join(GUIDE_COPY.panel.compareJoin)
  return (
    <a
      className={clsx(pinStyles.pin, pinStyles.tile, pinStyles.tileGo)}
      href={link.href}
      title={names}
      aria-label={`${link.label}: ${names}`}
    >
      <span>{link.label}</span>
      <small className={clsx(pinStyles.tileSub, pinStyles.tileNames)}>{names}</small>
    </a>
  )
}

const PickFocusBody: React.VFC<PickFocusBodyProps> = ({ focus, declared }) => {
  const { pick, placement, segment } = focus
  const { context } = pick
  const name = context?.name ?? pick.title
  const nodeId = context?.node_id ?? ''
  const yours = pickIsYours(pick, declared)
  const labId = pickNumbers(pick).clearbuyProductId ?? null
  const wants = wantsCurves(declared, yours, context) && labId !== null
  // The row fetched these as it scrolled in (`curves.ts` caches per id); the panel reads the
  // same cache, so a focus costs no second request.
  const curves = useCurves(labId, wants)
  const owners = cardOwnersFromCard(context?.owners)
  const trackOwner = useOwnerTrack({ nodeId, name })
  // The panel overlays the reader's curve — unless this IS their product, drawn alone.
  const overlay = yours ? null : declared
  const location = placement === 'section' ? 'pick' : placement
  let body: JSX.Element
  if (segment === 'owners') {
    body = owners ? (
      <PickOwners
        owners={owners}
        claimedHours={context?.compare.batteryHours ?? null}
        pdpHref={context?.sg_href ?? null}
        nodeId={nodeId}
        productName={name}
        surface="panel"
      />
    ) : (
      <p className={styles.loading}>{GUIDE_COPY.actions.ownersEmpty}</p>
    )
  } else if (curves) {
    body = (
      <PickLabPanel
        productName={name}
        pdpHref={context?.sg_href ?? null}
        curves={curves}
        declared={overlay}
        trackOwner={trackOwner}
        idPrefix={`panel-lab-${pick.clearBuyProductId}`}
        location={location}
      />
    )
  } else {
    body = (
      <p className={styles.loading}>
        {curves === undefined && wants ? 'Loading lab curves…' : 'No lab measurements to draw.'}
      </p>
    )
  }
  return <div className={styles.root}>{body}</div>
}

interface PickFocusFootProps {
  focus: PickFocus
  /** The pick is the reader's declared product: nothing to pin it against. */
  yours: boolean
  className?: string
}

/** The tile's guard: a pick DealHunt has joined, measured by the lab, and not the reader's own. */
function pinnable(context: GuidePickContext | null, yours: boolean): context is GuidePickContext {
  return Boolean(context && context.lab_tested && !yours)
}

/**
 * The panel's foot on "This pick", "Owners" and "Compare" alike: the pin tile on the left (on
 * Compare it shows the pick's pinned state), the compare tile on the right, half the width each;
 * either alone takes the whole foot. Nothing at all for a pick DealHunt hasn't joined, so the
 * drawer never draws an empty strip. `location` is the raw placement, as on the row
 * (`*_owner_pin`), never the lab panel's mapped one.
 */
export const PickFocusFoot: React.VFC<PickFocusFootProps> = ({ focus, yours, className }) => {
  const { pick, placement } = focus
  const { context } = pick
  if (!context) {
    return null
  }
  return (
    <div className={className} data-testid="lab-panel-foot">
      {pinnable(context, yours) && (
        <PinToCompare
          nodeId={context.node_id}
          slug={context.full_slug}
          name={context.name}
          pdpHref={context.sg_href}
          clearbuyProductId={context.compare.clearbuyProductId ?? pick.clearBuyProductId}
          requireDeclared={false}
          mode="toggle"
          location={placement}
          variant="tile"
        />
      )}
      <CompareTile segment={focus.segment} context={context} yours={yours} />
    </div>
  )
}

export default PickFocusBody
